'use client';

import { BentoCard, BentoCardContent } from '@/app/_components/core/bento-card';
import { Copytext } from '@/app/_components/core/copytext';
import { RespImage } from '@/app/_components/core/resp-image';
import { SmartLink } from '@/app/_components/core/smart-link';
import { CardCarouselElement } from '@/app/_components/shared/card-carousel/card-carousel.types';
import { useLocale } from '@/app/_contexts/locale-context';
import { formatDate } from '@/app/_lib/date-utils';
import { cn, emptyToUndefined } from '@/app/_lib/utils';

interface NewsCardProps {
  card: CardCarouselElement;
  className?: string;
  priority?: boolean;
  trackId?: string;
}

export function NewsCard({ card, className, priority, trackId }: NewsCardProps) {
  const locale = useLocale();
  const isLinked = !!card.detailLink;

  const hasImage =
    card.image?.srcsetCandidates &&
    (Array.isArray(card.image.srcsetCandidates)
      ? card.image.srcsetCandidates.length > 0
      : card.image.srcsetCandidates.landscape &&
        card.image.srcsetCandidates.landscape.length > 0);

  const imageSrcset = card.image?.srcsetCandidates
    ? Array.isArray(card.image.srcsetCandidates)
      ? card.image.srcsetCandidates
      : card.image.srcsetCandidates.landscape
    : undefined;

  const CardWrapper = (
    <BentoCard as="article" className={cn('group h-full', className)}>
      {hasImage && imageSrcset && (
        <div className="relative aspect-video w-full overflow-hidden bg-muted">
          <RespImage
            srcsetCandidates={imageSrcset}
            alt={emptyToUndefined(card.image?.altText) ?? emptyToUndefined(card.title) ?? ''}
            title={card.image?.title ?? undefined}
            sizes="(min-width: 1920px) 391px, (min-width: 1280px) 319px, (min-width: 1024px) 262px, (min-width: 768px) 318px, (min-width: 640px) 482px, calc((100vw - 16px) * 0.8 - 24px * 0.2)"
            priority={priority}
            className={cn(
              'size-full  object-cover transition-transform duration-300',
              isLinked && '[@media(hover:hover)]:group-hover:scale-105'
            )}
          />
        </div>
      )}
      <BentoCardContent className="grid grow gap-2 py-6">
        <div className="flex items-center justify-between text-sm font-medium leading-none text-card-foreground">
          {card.category && <span>{card.category}</span>}
          {card.date && (
            <time dateTime={card.date}>{formatDate(card.date, locale)}</time>
          )}
        </div>
        {card.title && (
          <Copytext variant="lead" className="mb-0">
            {card.title}
          </Copytext>
        )}
      </BentoCardContent>
    </BentoCard>
  );

  if (card.detailLink) {
    return (
      <SmartLink
        href={card.detailLink}
        download={card.detailLinkDownload}
        className="block h-full p-0.5"
        data-track-id={trackId}
      >
        {CardWrapper}
      </SmartLink>
    );
  }

  return CardWrapper;
}